import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { hasDuplicateLabel } from "./ReferenceDataList";
import { POWERS_WEIGHT_DEFAULT, densifyElementWeights } from "@/components/match/match-design-rules";
import { assertNoValidationRejection } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";
import {
  AdminTeamProfilesDocument,
  CreateTeamProfileDocument,
  DeleteTeamProfileDocument,
  UpdateTeamProfileDocument,
} from "@/graphql/generated/operations";
import type { CreativityElement } from "@/graphql/generated/operations";
import { KpiBadge } from "@/components/design/KpiBadge";
import { MenuItem } from "@/components/design/MenuItem";
import { AdminMasterDetail } from "./AdminMasterDetail";
import { isReadonlyTeamProfile } from "./team-profile-presets";
import { TeamProfileCard } from "./TeamProfileCard";
import type { TeamProfileDraft } from "./TeamProfileCard";

const PAGE_SIZE = 50;

function weightsInput(elementWeights: Record<string, number>) {
  return Object.entries(elementWeights).map(([element, weight]) => ({
    // Codegen emits CreativityElement in two files, so element keys are carried
    // as plain strings and narrowed here — see the note in lib/elements.ts.
    element: element as CreativityElement,
    weight,
  }));
}

// Admin > Stammdaten > Teamprofil ("Power Profil"): the TeamProfile list with
// its weights. Deliberately not an EDITABLE_REF_TYPES entry — that generic list
// covers flat label/id reference data and has nowhere to put weights.
export function TeamProfilePanel() {
  const intl = useIntl();
  const { data, loading, error: queryError, refetch } = useQuery(AdminTeamProfilesDocument, {
    variables: { input: { first: PAGE_SIZE } },
  });
  const [createMutation] = useMutation(CreateTeamProfileDocument);
  const [updateMutation] = useMutation(UpdateTeamProfileDocument);
  const [deleteMutation] = useMutation(DeleteTeamProfileDocument);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // null = "Neues Profil" steht rechts. Die Auswahl lebt hier und nicht in der
  // Liste, weil das Detail sie ebenfalls aufloesen muss.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftGerman, setDraftGerman] = useState("");
  const [draftEnglish, setDraftEnglish] = useState("");

  const profiles = data?.teamProfiles.nodes ?? [];
  const totalCount = data?.teamProfiles.totalCount ?? profiles.length;

  // Genesis accepts duplicate names, so the guard is client-side — same rule and
  // same wording as every other Stammdaten list (hasDuplicateLabel). Checked per
  // locale: two profiles may not collide in either language, or Match's picker
  // shows two entries nobody can tell apart.
  function isDuplicateName(german: string, english: string, exceptId?: string): boolean {
    const rows = (pick: (p: (typeof profiles)[number]) => string | null | undefined) =>
      profiles.map((p) => ({ id: p.id, label: pick(p) ?? "" }));
    return (
      hasDuplicateLabel(rows((p) => p.localizedName?.german), german, exceptId) ||
      hasDuplicateLabel(rows((p) => p.localizedName?.english), english, exceptId)
    );
  }

  const createDuplicate = isDuplicateName(draftGerman.trim(), draftEnglish.trim());

  /** Returns whether the write went through, so callers only clear inputs on success. */
  async function run(work: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const result = await work();
      assertNoValidationRejection(
        result,
        intl.formatMessage({
          id: "teamProfile.saveFailed",
          defaultMessage: "The profile could not be saved.",
        }),
      );
      await refetch();
      return true;
    } catch (e) {
      setError(userErrorMessage(intl, e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  // New profiles start at the neutral baseline across all 30 powers; the
  // weights are then tuned in the card's editor, so the add form stays a name.
  async function submitCreate() {
    const german = draftGerman.trim();
    const english = draftEnglish.trim();
    if (!german || !english || createDuplicate) return;
    const created = await run(() =>
      createMutation({
        variables: {
          input: {
            data: {
              localizedName: { german, english },
              weights: weightsInput(densifyElementWeights({}, POWERS_WEIGHT_DEFAULT)),
            },
          },
        },
      }),
    );
    // Only on success — a rejected create used to wipe both fields, leaving the
    // admin with an error message and no idea what they had typed.
    if (created) {
      setDraftGerman("");
      setDraftEnglish("");
    }
  }

  const selected = profiles.find((p) => p.id === selectedId) ?? null;
  // Ein geloeschtes oder weggefiltertes Profil darf nicht zu einer leeren
  // Detailspalte fuehren: dann faellt die Auswahl auf das Anlegen zurueck.
  if (selectedId !== null && !selected && !loading) setSelectedId(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-medium">
          {intl.formatMessage({ id: "teamProfile.title", defaultMessage: "Power Scorecards" })}
        </h2>
        <p className="mt-1 text-[12px] text-ink/55">
          {intl.formatMessage({
            id: "teamProfile.sub",
            defaultMessage:
              "Named weightings across the 30 powers, offered when designing a Match. Explore, Exploit and Ausgewogen are templates from Smycles: their name and weights are fixed.",
          })}
        </p>
      </div>

      {/* Query errors get their own banner: a failing list otherwise renders as
          the empty state, which reads as "there are no profiles" instead of
          "the list could not be loaded". */}
      {queryError ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {userErrorMessage(intl, queryError)}
        </div>
      ) : null}
      {error ? (
        <div className="rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>
      ) : null}

      <AdminMasterDetail
        title={intl.formatMessage({ id: "teamProfile.title", defaultMessage: "Power Scorecards" })}
        // totalCount, nicht profiles.length: die Liste zeigt eine feste Seite,
        // der Zaehler soll den Bestand nennen — sonst steht (50) ueber einer
        // Liste, die darunter selbst "die ersten 50 von 63" schreibt.
        count={totalCount}
        list={
          <>
            <Button variant="primary" className="mb-3 w-full" onClick={() => setSelectedId(null)}>
              {intl.formatMessage({ id: "teamProfile.create.title", defaultMessage: "New profile" })}
            </Button>

            {loading && profiles.length === 0 ? (
              <div className="text-[12px] text-ink/45">
                {intl.formatMessage({ id: "teamProfile.loading", defaultMessage: "Loading…" })}
              </div>
            ) : profiles.length === 0 ? (
              <div className="text-[12px] text-ink/45">
                {intl.formatMessage({ id: "teamProfile.empty", defaultMessage: "No profiles yet." })}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {profiles.map((profile) => {
                  const label = profile.localizedName?.localized || profile.name || profile.id;
                  return (
                    <MenuItem
                      key={profile.id}
                      active={profile.id === selectedId}
                      aria-pressed={profile.id === selectedId}
                      onClick={() => setSelectedId(profile.id)}
                      trailing={
                        isReadonlyTeamProfile(profile.id) ? (
                          // KpiBadge ohne onClick ist die etablierte read-only
                          // Tint-Pill einer Listenzeile (UI_CONVENTIONS
                          // "KpiBadge"); muted, damit sie nicht mit der
                          // Auswahltoenung der Zeile konkurriert.
                          <KpiBadge tone="muted">
                            {intl.formatMessage({
                              id: "teamProfile.presetBadge",
                              defaultMessage: "Template",
                            })}
                          </KpiBadge>
                        ) : null
                      }
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </div>
            )}

            {/* The query takes a fixed page, and nothing else caps how many profiles
                an admin can create — say so rather than let the tail vanish. */}
            {totalCount > profiles.length ? (
              <div className="mt-3 text-[12px] text-ink/55">
                {intl.formatMessage(
                  {
                    id: "teamProfile.truncated",
                    defaultMessage:
                      "Showing the first {shown} of {total} profiles. Delete or rename profiles you no longer need.",
                  },
                  { shown: profiles.length, total: totalCount },
                )}
              </div>
            ) : null}
          </>
        }
      >
        {selected ? (
          <TeamProfileCard
            key={selected.id}
            profile={selected}
            busy={busy}
            isDuplicateName={(german, english) => isDuplicateName(german, english, selected.id)}
            onSave={(draft: TeamProfileDraft) =>
              void run(() =>
                updateMutation({
                  variables: {
                    id: selected.id,
                    input: {
                      data: {
                        localizedName: { german: draft.german.trim(), english: draft.english.trim() },
                        weights: weightsInput(draft.elementWeights),
                      },
                    },
                  },
                }),
              )
            }
            onDelete={() =>
              void run(() => deleteMutation({ variables: { input: { id: selected.id } } }))
            }
          />
        ) : (
          <div className="box bg-surface p-5">
            <div className="text-sm font-medium">
              {intl.formatMessage({ id: "teamProfile.create.title", defaultMessage: "New profile" })}
            </div>
            <div className="mt-1 text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "teamProfile.create.sub",
                defaultMessage:
                  "Starts with every power weighted equally. Open the profile afterwards to tune the weights.",
              })}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 @sm:grid-cols-2">
              <input
                value={draftGerman}
                onChange={(e) => setDraftGerman(e.target.value)}
                disabled={busy}
                placeholder={intl.formatMessage({
                  id: "teamProfile.nameDe",
                  defaultMessage: "Name (German)",
                })}
                aria-label={intl.formatMessage({ id: "teamProfile.nameDe", defaultMessage: "Name (German)" })}
                className="bg-surface w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30 disabled:opacity-60"
              />
              <input
                value={draftEnglish}
                onChange={(e) => setDraftEnglish(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submitCreate();
                }}
                disabled={busy}
                placeholder={intl.formatMessage({
                  id: "teamProfile.nameEn",
                  defaultMessage: "Name (English)",
                })}
                aria-label={intl.formatMessage({ id: "teamProfile.nameEn", defaultMessage: "Name (English)" })}
                className="bg-surface w-full rounded-md px-3 py-2 text-sm ring-1 ring-ink/10 focus:outline-none focus:ring-ink/30 disabled:opacity-60"
              />
            </div>
            {createDuplicate ? (
              <div className="mt-2 text-[12px] text-ink/55">
                {intl.formatMessage({
                  id: "teamProfile.duplicate",
                  defaultMessage: "A profile with this name already exists.",
                })}
              </div>
            ) : null}
            <Button
              variant="primary"
              size="compact"
              className="mt-3"
              disabled={
                busy ||
                createDuplicate ||
                draftGerman.trim().length === 0 ||
                draftEnglish.trim().length === 0
              }
              onClick={() => void submitCreate()}
            >
              {intl.formatMessage({ id: "teamProfile.create.submit", defaultMessage: "Create profile" })}
            </Button>
          </div>
        )}
      </AdminMasterDetail>
    </div>
  );
}
